import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, API_BASE, apiFetch } from "@/lib/api";
import {
  Plus, Trash, PencilSimple, X, BookOpen, CaretDown, CaretUp,
  ArrowSquareOut, FloppyDisk, Eye, GridFour, Warning, Question, FilmSlate, DotsSixVertical, CursorClick,
  Translate, Check, Clock, ArrowUUpLeft,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import { usePillars, pillarChipStyle } from "@/contexts/PillarsContext";
import { toast } from "sonner";
import { useAutoDraft } from "@/hooks/useAutoDraft";

const SCHOOL_GRADES = ["G7", "G8", "G9", "G10", "G11", "G12"];
const GRADES = [...SCHOOL_GRADES, "DEMO"];
const gLabel = (g) => (g === "DEMO" ? "Demo" : g);
const INTRO_KEY = "__intro__";

export default function CourseBuilderPanel({ courses, onRefresh, canDelete, initialOpenCourseId }) {
  const { labelOf, colorOf } = usePillars();
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openId, setOpenId] = useState(initialOpenCourseId || null);
  const [err, setErr] = useState("");
  const [trash, setTrash] = useState([]);
  const [assetLangMap, setAssetLangMap] = useState({}); // asset_id -> ["fr", ...] language versions
  const [assetDurMap, setAssetDurMap] = useState({}); // asset_id -> duration_minutes
  const [recalcBusy, setRecalcBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/lesson-assets").then((r) => {
      const m = {};
      const d = {};
      (r.data || []).forEach((a) => {
        const codes = [...new Set((a.swap_rules || []).filter((x) => x.scope === "language" && x.scope_value).map((x) => x.scope_value))];
        if (codes.length) m[a.id] = codes;
        d[a.id] = Number(a.duration_minutes) || 0;
      });
      setAssetLangMap(m);
      setAssetDurMap(d);
    }).catch(() => {});
  }, [courses]);

  // Live course runtime: sum of every lesson's duration (auto-updates when lessons change).
  const totalDuration = (c) => (c.lessons || []).reduce((s, ls) => s + (assetDurMap[ls.lesson_asset_id] || 0), 0);
  const fmtDuration = (mins) => (mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins} min`);

  // Per-course translation coverage: {fr: 12, ...} counting lessons with that language version.
  const langCounts = (c) => {
    const counts = {};
    (c.lessons || []).forEach((ls) => (assetLangMap[ls.lesson_asset_id] || []).forEach((code) => { counts[code] = (counts[code] || 0) + 1; }));
    return counts;
  };

  const loadTrash = async () => {
    if (!canDelete) return;
    try { const r = await api.get("/admin/courses-trash"); setTrash(r.data); } catch { /* non-blocking */ }
  };
  useEffect(() => { loadTrash(); /* eslint-disable-next-line */ }, [courses.length, canDelete]);

  const restoreCourse = async (id) => {
    try {
      await api.post(`/admin/courses/${id}/restore`);
      await onRefresh(); await loadTrash();
      toast.success("Course restored — quizzes and matrix intact");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (initialOpenCourseId) {
      setOpenId(initialOpenCourseId);
      const t = setTimeout(() => {
        document.querySelector(`[data-testid="course-row-${initialOpenCourseId}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [initialOpenCourseId]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this course? Lessons in the library are preserved.")) return;
    setErr("");
    const title = courses.find(c => c.id === id)?.title || "Course";
    try {
      await api.delete(`/admin/courses/${id}`);
      onRefresh();
      toast(`"${title}" deleted`, {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try { await api.post(`/admin/courses/${id}/restore`); onRefresh(); toast.success("Course restored — quizzes and matrix intact"); }
            catch (e) { toast.error(formatApiError(e)); }
          },
        },
      });
    }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold font-display">Courses ({courses.length})</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="recalc-durations-button" className="btn-secondary flex items-center gap-1 text-sm" disabled={recalcBusy}
            onClick={async () => {
              if (!window.confirm("Recalculate every lesson's duration from its video runtime (+30s)? This overwrites manually entered durations.")) return;
              setRecalcBusy(true);
              try {
                const { data } = await api.post("/admin/lesson-assets/recalc-durations");
                await onRefresh();
                const stillEncoding = (data.skipped || []).filter((s) => (s.reason || "").includes("transcoding")).length;
                toast.success(`Durations recalculated — ${data.updated} lesson${data.updated === 1 ? "" : "s"} updated${stillEncoding ? `, ${stillEncoding} still transcoding (run again later)` : ""}`);
              } catch (e) { toast.error(formatApiError(e)); }
              finally { setRecalcBusy(false); }
            }}>
            <Clock size={16} /> {recalcBusy ? "Recalculating…" : "Recalc durations"}
          </button>
          <button data-testid="new-course-button" className="btn-primary flex items-center gap-1" onClick={() => { setEditing(null); setShow(true); }}>
            <Plus size={16} weight="bold" /> New course
          </button>
        </div>
      </div>
      <p className="text-sm text-[#000000] mb-3">
        Build a course, then use the per-grade <strong>visibility matrix</strong> to control which lessons each grade (7–12) sees. Click a lesson to open its full editor.
      </p>

      {err && <div className="text-[#FF5100] mb-3 text-sm">{err}</div>}

      <AnimatePresence>
        {show && (
          <CourseForm
            initial={editing}
            onCancel={() => { setShow(false); setEditing(null); }}
            onSaved={async (c) => { setShow(false); setEditing(null); await onRefresh(); setOpenId(c.id); }}
          />
        )}
      </AnimatePresence>

      <div className="flex flex-col gap-3">
        {courses.map(c => (
          <div key={c.id} className="rounded-2xl border border-[#C4C8CE] bg-[#DCDEE2]" data-testid={`course-row-${c.id}`}>
            <div className="flex items-center justify-between gap-3 p-4">
              <button
                data-testid={`expand-course-${c.id}`}
                className="flex-1 flex items-center gap-3 text-left bg-transparent border-0 cursor-pointer"
                onClick={() => setOpenId(openId === c.id ? null : c.id)}
              >
                <BookOpen size={20} weight="duotone" color="#CCFF00" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold font-display truncate">{c.title}</div>
                  <div className="text-xs text-[#000000] flex flex-wrap gap-2 mt-0.5">
                    <span>{c.lesson_count ?? (c.lessons || []).length} lessons</span>
                    {totalDuration(c) > 0 && (
                      <span data-testid={`course-total-duration-${c.id}`} title="Total course runtime — sum of every lesson's duration">
                        {fmtDuration(totalDuration(c))}
                      </span>
                    )}
                    <span>${(c.price_cents / 100).toFixed(2)}</span>
                    {Object.entries(langCounts(c)).map(([code, n]) => (
                      <span key={code} data-testid={`course-lang-coverage-${c.id}-${code}`}
                        title={`${n} of ${(c.lessons || []).length} lessons have a ${code.toUpperCase()} version`}
                        className="inline-flex items-center gap-0.5 rounded-full font-bold"
                        style={{ padding: "0 0.45rem", fontSize: "0.6rem", background: "rgba(0,184,212,0.12)", color: "#007A8F", border: "1px solid #00B8D4" }}>
                        <Translate size={9} weight="bold" /> {code.toUpperCase()}: {n}/{(c.lessons || []).length}
                      </span>
                    ))}
                    {(c.pillars || []).map(p => <span key={p} className="chip" style={{ ...pillarChipStyle(colorOf(p)), padding: "0 0.4rem", fontSize: "0.6rem" }}>{labelOf(p)}</span>)}
                  </div>
                </div>
                {openId === c.id ? <CaretUp size={18} color="#52525B" /> : <CaretDown size={18} color="#52525B" />}
              </button>
              <div className="flex gap-1">
                <button data-testid={`edit-course-${c.id}`} className="btn-ghost p-1.5" title="Edit" onClick={() => { setEditing(c); setShow(true); }}>
                  <PencilSimple size={16} />
                </button>
                {canDelete && (
                  <button data-testid={`delete-course-${c.id}`} className="btn-ghost p-1.5" title="Delete" onClick={() => onDelete(c.id)}>
                    <Trash size={16} color="#FF5100" />
                  </button>
                )}
              </div>
            </div>
            {openId === c.id && <CourseMatrix course={c} onRefresh={onRefresh} />}
          </div>
        ))}
        {courses.length === 0 && <div className="text-[#000000] text-sm p-4 text-center">No courses yet. Click "New course".</div>}
      </div>

      {canDelete && trash.length > 0 && (
        <div className="mt-5 rounded-2xl border border-dashed border-[#C4C8CE] p-4" data-testid="recently-deleted-section">
          <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-2 flex items-center gap-1.5">
            <Trash size={14} /> Recently deleted · restorable for 30 days
          </div>
          <div className="flex flex-col gap-2">
            {trash.map(t => (
              <div key={t.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-[#F4F4F5] border border-[#C4C8CE]" data-testid={`trash-course-${t.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{t.title}</div>
                  <div className="text-xs text-[#000000]">
                    {t.lesson_count} lessons · deleted {new Date(t.deleted_at).toLocaleDateString()} · {t.days_left} day{t.days_left === 1 ? "" : "s"} left to restore
                  </div>
                </div>
                <button className="btn-secondary text-xs" data-testid={`restore-course-${t.id}`} onClick={() => restoreCourse(t.id)}>Restore</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CourseForm({ initial, onCancel, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const { pillars: allPillars } = usePillars();
  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    cover_image_url: initial?.cover_image_url || "",
    price_cents: initial?.price_cents ?? 0,
    pricing_model: initial?.pricing_model || "pay_once",
    is_published: initial?.is_published ?? true,
    celebrations_enabled: initial?.celebrations_enabled ?? true,
    pillars: initial?.pillars || [],
    translations: initial?.translations || {},
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [coverBusy, setCoverBusy] = useState(false);
  const coverRef = React.useRef(null);

  const uploadCover = async (f) => {
    if (!f) return;
    setErr(""); setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await apiFetch(`${API_BASE}/admin/settings/upload-image`, { method: "POST", body: fd });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Upload failed"); }
      const d = await res.json();
      setForm(prev => ({ ...prev, cover_image_url: d.url }));
    } catch (e) { setErr(e.message); }
    finally { setCoverBusy(false); if (coverRef.current) coverRef.current.value = ""; }
  };

  const { restored: draftRestored, reset: resetDraft } = useAutoDraft({
    key: `saproject:course-draft:${initial?.id || "new"}`,
    value: form,
    ready: true,
    onRestore: (d) => setForm(d),
  });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const res = isEdit
        ? await api.patch(`/admin/courses/${initial.id}`, form)
        : await api.post("/admin/courses", form);
      resetDraft();
      onSaved(res.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const handleCancel = () => { resetDraft(); onCancel(); };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="mb-4 p-4 rounded-2xl bg-[#DCDEE2] border border-[#C4C8CE] flex flex-col gap-3"
      data-testid="course-form"
    >
      <div className="flex items-center justify-between">
        <div className="font-bold font-display">{isEdit ? "Edit course" : "Create new course"}</div>
        <button type="button" className="btn-ghost p-1" onClick={handleCancel}><X size={18} /></button>
      </div>
      {draftRestored && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[#CCFF00]/15 border border-[#CCFF00]/40 text-xs" data-testid="course-draft-restored">
          <span className="text-[#000000]">Restored your unsaved course details.</span>
          <button type="button" className="underline text-[#000000]" onClick={() => { resetDraft(); setForm({ title: "", description: "", cover_image_url: "", price_cents: 0, is_published: true, celebrations_enabled: true }); }} data-testid="course-discard-draft">Discard</button>
        </div>
      )}
      <input data-testid="course-title-input" className="input-base" placeholder="Course title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      <textarea data-testid="course-description-input" rows={3} className="input-base" placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      <CourseTranslationsField form={form} setForm={setForm} />
      <div className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-[0.12em] text-[#000000]">Cover image</span>
        <div className="flex items-center gap-2 flex-wrap">
          {form.cover_image_url ? (
            <img src={form.cover_image_url} alt="cover preview" className="w-20 h-14 object-cover rounded-lg border border-[#C4C8CE]" data-testid="course-cover-preview" />
          ) : (
            <div className="w-20 h-14 rounded-lg border border-dashed border-[#C4C8CE] grid place-items-center text-[10px] text-[#000000]">No image</div>
          )}
          <input ref={coverRef} type="file" accept=".png,.jpg,.jpeg,.webp,.gif" className="hidden" data-testid="course-cover-file" onChange={(e) => uploadCover(e.target.files?.[0])} />
          <button type="button" className="btn-secondary text-sm" onClick={() => coverRef.current?.click()} disabled={coverBusy} data-testid="course-cover-upload-btn">
            {coverBusy ? "Uploading…" : (form.cover_image_url ? "Replace image" : "Upload image")}
          </button>
          {form.cover_image_url && (
            <button type="button" className="btn-ghost text-sm text-[#FF5100]" onClick={() => setForm({ ...form, cover_image_url: "" })} data-testid="course-cover-clear">Remove</button>
          )}
        </div>
        <span className="text-[11px] text-[#000000]" data-testid="course-cover-hint">Recommended 1280×720 (16:9 landscape) · PNG, JPG or WEBP · under 5&nbsp;MB</span>
        <input data-testid="course-cover-input" className="input-base text-xs" placeholder="…or paste an image URL" value={form.cover_image_url || ""} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="input-base flex items-center text-xs text-[#000000] bg-[#F4F4F5]" data-testid="course-price-note" title="Set the public price on the Master catalogue product linked to this course">
          Price comes from the Master catalogue
        </div>
        <select data-testid="course-pricing-model" className="input-base" value={form.pricing_model || "pay_once"} onChange={(e) => setForm({ ...form, pricing_model: e.target.value })} title="How adult (public) buyers pay for this course">
          <option value="pay_once">Public: pay once (forever)</option>
          <option value="annual">Public: annual pass (365 days)</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-[#000000]">
          <input data-testid="course-published-toggle" type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
          Published
        </label>
      </div>
      <label className="flex items-start gap-2 text-sm text-[#000000] p-3 rounded-xl bg-[#FFFFFF] border border-[#C4C8CE]">
        <input data-testid="course-celebrations-toggle" type="checkbox" className="mt-0.5" checked={form.celebrations_enabled} onChange={(e) => setForm({ ...form, celebrations_enabled: e.target.checked })} />
        <span>
          <span className="font-semibold text-[#000000]">Celebrations &amp; rewards</span>
          <span className="block text-[11px] text-[#000000]">Confetti, sounds and bonus-XP badges when learners finish videos and pass quizzes. Turn off if it feels like too much.</span>
        </span>
      </label>
      <div className="flex flex-col gap-2 p-3 rounded-xl bg-[#FFFFFF] border border-[#C4C8CE]">
        <span className="text-xs uppercase tracking-[0.12em] text-[#000000]">Sections in this course</span>
        <span className="text-[11px] text-[#000000] -mt-1">Pick which sections appear as columns. Leave all off to include every section (great for a full course); select just a few for a mini course.</span>
        <div className="flex flex-wrap gap-2 mt-1" data-testid="course-pillar-picker">
          {allPillars.length === 0 && <span className="text-xs italic text-[#000000]">No sections yet — create some in the Pillars tab.</span>}
          {allPillars.map((p) => {
            const on = form.pillars.includes(p.slug);
            return (
              <button
                key={p.slug}
                type="button"
                data-testid={`course-pillar-toggle-${p.slug}`}
                onClick={() => setForm((f) => ({ ...f, pillars: on ? f.pillars.filter((s) => s !== p.slug) : [...f.pillars, p.slug] }))}
                className="chip text-xs font-bold transition-all"
                style={{
                  background: on ? p.color : "#FFFFFF",
                  color: on ? "#0A0D14" : "#52525B",
                  border: "1px solid " + (on ? p.color : "#C4C8CE"),
                  cursor: "pointer",
                }}
              >
                {on ? "✓ " : ""}{p.name}
              </button>
            );
          })}
        </div>
      </div>
      {err && <div className="text-[#FF5100] text-sm">{err}</div>}
      <div className="flex gap-2">
        <button type="submit" className="btn-primary" disabled={busy} data-testid="submit-course-form">{busy ? "Saving..." : (isEdit ? "Save changes" : "Create course")}</button>
        <button type="button" className="btn-secondary" onClick={handleCancel}>Cancel</button>
      </div>
    </motion.form>
  );
}

// Per-language course info (title/description). A dropdown lists the app's extra languages —
// picking one opens a popup, mirroring how alternative lesson versions are authored.
function CourseTranslationsField({ form, setForm }) {
  const [langs, setLangs] = useState([]);
  const [editing, setEditing] = useState(null); // language code being edited

  useEffect(() => {
    api.get("/translations").then((r) => setLangs(r.data || [])).catch(() => setLangs([]));
  }, []);
  if (!langs.length) return null;

  const filled = (code) => {
    const tr = (form.translations || {})[code];
    return !!(tr && (tr.title || tr.description));
  };
  const editingLang = langs.find((l) => l.lang === editing);

  return (
    <div className="flex flex-col gap-2 p-3 rounded-xl bg-[#FFFFFF] border border-[#C4C8CE]" data-testid="course-translations-field">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-[0.12em] text-[#000000] flex items-center gap-1.5">
          <Translate size={14} color="#00B8D4" /> Course info in other languages
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="btn-secondary text-xs flex items-center gap-1" style={{ padding: "4px 12px" }} data-testid="course-translations-dropdown">
              <Translate size={13} /> Add / edit translation <CaretDown size={11} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {langs.map((l) => (
              <DropdownMenuItem key={l.lang} className="cursor-pointer gap-2" data-testid={`course-translation-lang-${l.lang}`}
                onSelect={(e) => { e.preventDefault(); setEditing(l.lang); }}>
                {filled(l.lang) ? <Check size={14} weight="bold" color="#00A550" /> : <span className="w-[14px]" />} {l.label || l.lang}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <span className="text-[11px] text-[#000000] -mt-1">Students who switch the app to that language see this title &amp; description instead. Lesson-by-lesson translations live in each lesson's "Lesson versions" section.</span>
      {langs.filter((l) => filled(l.lang)).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {langs.filter((l) => filled(l.lang)).map((l) => (
            <button key={l.lang} type="button" className="chip text-xs font-bold" data-testid={`course-translation-chip-${l.lang}`}
              style={{ background: "#CCFF00", color: "#000000", border: "1px solid #CCFF00", cursor: "pointer" }}
              onClick={() => setEditing(l.lang)}>
              ✓ {l.label || l.lang}
            </button>
          ))}
        </div>
      )}
      {editing && (
        <CourseTranslationSheet
          label={editingLang?.label || editing}
          initial={(form.translations || {})[editing] || {}}
          englishTitle={form.title}
          englishDescription={form.description}
          onSave={(tr) => {
            setForm((f) => ({ ...f, translations: { ...(f.translations || {}), [editing]: tr } }));
            setEditing(null);
          }}
          onClear={() => {
            setForm((f) => {
              const next = { ...(f.translations || {}) };
              delete next[editing];
              return { ...f, translations: next };
            });
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function CourseTranslationSheet({ label, initial, englishTitle, englishDescription, onSave, onClear, onClose }) {
  const [title, setTitle] = useState(initial.title || "");
  const [description, setDescription] = useState(initial.description || "");
  const hasExisting = !!(initial.title || initial.description);

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto bg-[#E8EAED] text-[#000000]" data-testid="course-translation-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-[#000000] flex items-center gap-2"><Translate size={20} color="#00B8D4" /> {label} — course info</SheetTitle>
          <SheetDescription className="text-[#000000]">Fill this in bit by bit — anything left empty falls back to the English version.</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="card flex flex-col gap-2">
            <label className="text-[10px] uppercase tracking-[0.12em] text-[#000000]">Course title ({label})</label>
            <div className="text-[11px] text-[#000000] -mt-1">English: {englishTitle || "—"}</div>
            <input className="input-base" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={englishTitle || "Course title"} data-testid="course-translation-title" />
          </div>
          <div className="card flex flex-col gap-2">
            <label className="text-[10px] uppercase tracking-[0.12em] text-[#000000]">Short description ({label})</label>
            <div className="text-[11px] text-[#000000] -mt-1">English: {englishDescription || "—"}</div>
            <textarea rows={4} className="input-base" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={englishDescription || "Short description"} data-testid="course-translation-description" />
          </div>
        </div>
        <SheetFooter>
          {hasExisting && (
            <button type="button" className="btn-ghost text-sm text-[#FF5100]" onClick={onClear} data-testid="course-translation-clear">Remove this translation</button>
          )}
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" onClick={() => onSave({ title: title.trim(), description: description.trim() })} data-testid="course-translation-save">Save</button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function CourseMatrix({ course, onRefresh }) {
  const [warnStats, setWarnStats] = useState({});
  useEffect(() => {
    api.get("/admin/heavy-warning-stats").then(r => setWarnStats(r.data.stats || {})).catch(() => {});
  }, []);
  const nav = useNavigate();
  const { pillars, labelOf, colorOf, introLabel } = usePillars();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [grid, setGrid] = useState({});      // asset_id -> Set(grades)
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");  // "" or a grade
  const [editGrade, setEditGrade] = useState("");  // "" or a grade: click a card to toggle this grade
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const a = await api.get("/admin/lesson-assets");
      setAssets(a.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [course.id]);

  // Build grade grid from course lessons
  useEffect(() => {
    const g = {};
    (course.lessons || []).forEach(ls => {
      g[ls.lesson_asset_id] = new Set(ls.grades || SCHOOL_GRADES);
    });
    setGrid(g); setDirty(false);
  }, [course.id, course.lessons]);

  const assetMap = useMemo(() => Object.fromEntries(assets.map(a => [a.id, a])), [assets]);
  const lessons = useMemo(() => [...(course.lessons || [])].sort((a, b) => a.position - b.position), [course.lessons]);

  // Columns = intro + the course's chosen sections (ordered). Existing courses with no explicit
  // selection fall back to ALL sections, so their matrices are unchanged.
  const columns = useMemo(() => {
    const chosen = course.pillars || [];
    const active = chosen.length ? pillars.filter(p => chosen.includes(p.slug)) : pillars;
    return [{ key: INTRO_KEY, name: introLabel, color: "#52525B" }, ...active.map(p => ({ key: p.slug, name: p.name, color: p.color }))];
  }, [pillars, introLabel, course.pillars]);

  // Group lessons by column, each ordered by position → a true grid (row r = every column's r-th lesson).
  const columnGroups = useMemo(() => {
    const g = {}; columns.forEach(c => { g[c.key] = []; });
    lessons.forEach((ls) => {
      const k = assetMap[ls.lesson_asset_id]?.pillar || INTRO_KEY;
      (g[k] = g[k] || []).push(ls);
    });
    return g;
  }, [lessons, assetMap, columns]);
  const maxRows = useMemo(() => columns.reduce((m, c) => Math.max(m, (columnGroups[c.key] || []).length), 0), [columns, columnGroups]);

  // Drag to reorder within a pillar OR move a lesson to a different pillar (changes the asset's pillar).
  const dragRef = useRef(null);            // dragged lesson_asset_id
  const [dragOverCell, setDragOverCell] = useState(null); // `${colKey}:${row}`
  const moveCard = async (fromId, targetColKey, targetCardId) => {
    if (!fromId) return;
    const asset = assetMap[fromId];
    const currentCol = asset?.pillar || INTRO_KEY;
    if (targetColKey === currentCol && (fromId === targetCardId || !targetCardId && (columnGroups[targetColKey] || []).slice(-1)[0]?.lesson_asset_id === fromId)) return;
    let ids = lessons.map(l => l.lesson_asset_id).filter(id => id !== fromId);
    if (targetCardId && targetCardId !== fromId) {
      const idx = ids.indexOf(targetCardId);
      ids.splice(Math.max(0, idx), 0, fromId);           // insert before the card dropped onto
    } else {
      let lastIdx = -1;
      ids.forEach((id, i) => { if ((assetMap[id]?.pillar || INTRO_KEY) === targetColKey) lastIdx = i; });
      ids.splice(lastIdx + 1, 0, fromId);                // append to end of target column
    }
    try {
      if (targetColKey !== currentCol) {
        await api.patch(`/admin/lesson-assets/${fromId}`, { pillar: targetColKey === INTRO_KEY ? null : targetColKey });
      }
      await api.put(`/admin/courses/${course.id}/lessons/reorder`, ids);
      await onRefresh();
    } catch (e) { setErr(formatApiError(e)); }
    finally { dragRef.current = null; setDragOverCell(null); }
  };

  const toggleGrade = (assetId, grade) => {
    setGrid(prev => {
      const next = { ...prev };
      const s = new Set(next[assetId] || SCHOOL_GRADES);
      s.has(grade) ? s.delete(grade) : s.add(grade);
      next[assetId] = s;
      return next;
    });
    setDirty(true);
  };

  const snapshotGrid = (g) => Object.fromEntries(Object.entries(g).map(([k, s]) => [k, new Set(s)]));
  const undoGridToast = (label, before) => {
    toast(label, {
      duration: 10000,
      action: {
        label: "Undo",
        onClick: () => { setGrid(before); setDirty(true); toast.success("Grades reverted (unsaved — click Save matrix to keep)"); },
      },
    });
  };

  const bulkGrade = (grade, on) => {
    const before = snapshotGrid(grid);
    setGrid(prev => {
      const next = {};
      lessons.forEach(ls => {
        const s = new Set(prev[ls.lesson_asset_id] || SCHOOL_GRADES);
        on ? s.add(grade) : s.delete(grade);
        next[ls.lesson_asset_id] = s;
      });
      return next;
    });
    setDirty(true);
    undoGridToast(`${grade} turned ${on ? "on" : "off"} for all lessons`, before);
  };

  const allGradesReset = () => {
    const before = snapshotGrid(grid);
    setGrid(() => {
      const next = {};
      lessons.forEach(ls => { next[ls.lesson_asset_id] = new Set(SCHOOL_GRADES); });
      return next;
    });
    setDirty(true);
    undoGridToast("All grades reset to on", before);
  };

  const save = async () => {
    setSaving(true); setErr("");
    const prevMatrix = {};
    (course.lessons || []).forEach(ls => { prevMatrix[ls.lesson_asset_id] = ls.grades || [...SCHOOL_GRADES]; });
    try {
      const matrix = {};
      Object.entries(grid).forEach(([aid, set]) => { matrix[aid] = [...set]; });
      await api.put(`/admin/courses/${course.id}/grade-matrix`, { matrix });
      await onRefresh();
      setDirty(false);
      toast("Grade matrix saved", {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.put(`/admin/courses/${course.id}/grade-matrix`, { matrix: prevMatrix });
              await onRefresh();
              toast.success("Matrix reverted to its previous saved state");
            } catch (e) { toast.error(formatApiError(e)); }
          },
        },
      });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const addLesson = async (asset_id) => {
    try { await api.post(`/admin/courses/${course.id}/lessons`, { lesson_asset_id: asset_id }); await onRefresh(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const removeLesson = async (asset_id) => {
    try {
      const { data } = await api.delete(`/admin/courses/${course.id}/lessons/${asset_id}`);
      await onRefresh();
      const removed = data?.removed;
      if (removed) {
        toast(`"${assetMap[asset_id]?.title || "Lesson"}" removed from course`, {
          duration: 10000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                await api.post(`/admin/courses/${course.id}/lessons`, {
                  lesson_asset_id: removed.lesson_asset_id,
                  position: removed.position,
                  grades: removed.grades,
                });
                await onRefresh();
                toast.success("Lesson restored to its original spot");
              } catch (e) { toast.error(formatApiError(e)); }
            },
          },
        });
      }
    }
    catch (e) { setErr(formatApiError(e)); }
  };

  const used = new Set(lessons.map(l => l.lesson_asset_id));
  const available = assets.filter(a => !used.has(a.id));
  const visibleForPreview = (assetId) => !preview || (grid[assetId] || new Set()).has(preview);

  return (
    <div className="border-t border-[#C4C8CE] p-4 flex flex-col gap-4" data-testid={`course-matrix-${course.id}`}>
      {err && <div className="text-[#FF5100] text-sm">{err}</div>}

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-[0.15em] text-[#000000] flex items-center gap-1.5 mr-auto">
          <GridFour size={14} /> Visibility matrix · {lessons.length} lessons · ~{(() => { const m = lessons.reduce((s, l) => s + (Number(assetMap[l.lesson_asset_id]?.duration_minutes) || 0), 0); return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}`; })()} total · drag <DotsSixVertical size={12} /> to reorder or move a lesson to another pillar
        </div>
        <button data-testid={`create-lesson-${course.id}`} className="btn-primary text-sm flex items-center gap-1" onClick={() => nav(`/admin/lesson/new?course=${course.id}`)}>
          <Plus size={14} weight="bold" /> Create lesson
        </button>
        <button data-testid={`add-existing-lesson-${course.id}`} className="btn-secondary text-sm flex items-center gap-1" onClick={() => setShowAdd(s => !s)}>
          <BookOpen size={14} /> Add from library
        </button>
        <label className="flex items-center gap-1 text-xs text-[#000000] bg-[#FFFFFF] border border-[#C4C8CE] rounded-full px-2 py-1">
          <Eye size={14} />
          <select data-testid={`preview-grade-${course.id}`} className="bg-transparent text-xs outline-none" value={preview} onChange={(e) => setPreview(e.target.value)}>
            <option value="">Preview: all</option>
            {GRADES.map(g => <option key={g} value={g}>View as {gLabel(g)}</option>)}
          </select>
        </label>
        <button data-testid={`save-matrix-${course.id}`} className="btn-primary text-sm flex items-center gap-1 disabled:opacity-40" onClick={save} disabled={!dirty || saving}>
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : (dirty ? "Save matrix" : "Saved")}
        </button>
      </div>
      <button
        data-testid={`goto-lesson-matrix-${course.id}`}
        className="self-start text-xs font-semibold text-[#000000] hover:text-[#000000] underline underline-offset-2 flex items-center gap-1 bg-transparent border-0 cursor-pointer p-0"
        onClick={() => nav("/admin?tab=matrix")}
      >
        <GridFour size={13} /> Manage school/public visibility → Lesson Matrix
      </button>

      {/* Bulk grade actions */}
      <div className="flex items-center gap-1.5 flex-wrap text-xs">
        <span className="text-[#000000] uppercase tracking-[0.1em]">Bulk:</span>
        {GRADES.map(g => (
          <span key={g} className="inline-flex items-center rounded-full border border-[#C4C8CE] overflow-hidden">
            <span className="px-1.5 font-display font-bold text-[#000000]">{gLabel(g)}</span>
            <button data-testid={`bulk-on-${course.id}-${g}`} className="px-1.5 py-0.5 bg-[#CCFF00] text-[#000000] font-bold" onClick={() => bulkGrade(g, true)} title={`Turn ${gLabel(g)} on for all`}>on</button>
            <button data-testid={`bulk-off-${course.id}-${g}`} className="px-1.5 py-0.5 bg-[#FFFFFF] text-[#000000]" onClick={() => bulkGrade(g, false)} title={`Turn ${gLabel(g)} off for all`}>off</button>
          </span>
        ))}
        <button className="btn-ghost text-xs px-2 py-0.5 underline" onClick={allGradesReset} data-testid={`reset-grades-${course.id}`}>Reset all on</button>
      </div>

      {/* Quick-select: pick a grade, then click lessons to toggle it on/off */}
      <div className="flex items-center gap-2 flex-wrap text-xs rounded-xl px-3 py-2 border"
        style={{ background: editGrade ? "#F7FEE7" : "#FFFFFF", borderColor: editGrade ? "#BEF264" : "#C4C8CE" }}
        data-testid={`grade-quickselect-${course.id}`}>
        <span className="text-[#3F6212] font-bold uppercase tracking-[0.1em] flex items-center gap-1">
          <CursorClick size={14} weight="bold" /> Quick-select grade
        </span>
        <button data-testid={`edit-grade-off-${course.id}`} onClick={() => setEditGrade("")}
          data-state={editGrade === "" ? "on" : "off"}
          className="px-2 py-0.5 rounded-full font-display font-bold border transition-colors"
          style={{ background: editGrade === "" ? "#0A0D14" : "#FFFFFF", color: editGrade === "" ? "#FFFFFF" : "#52525B", borderColor: "#C4C8CE" }}>
          Off
        </button>
        {GRADES.map(g => {
          const on = editGrade === g;
          return (
            <button key={g} data-testid={`edit-grade-${course.id}-${g}`} onClick={() => setEditGrade(on ? "" : g)}
              data-state={on ? "on" : "off"}
              className="px-2.5 py-0.5 rounded-full font-display font-bold border transition-colors"
              style={{ background: on ? "#CCFF00" : "#FFFFFF", color: on ? "#1F2937" : "#52525B", borderColor: on ? "#CCFF00" : "#C4C8CE" }}>
              {gLabel(g)}
            </button>
          );
        })}
        {editGrade
          ? <>
              <button data-testid={`quickselect-all-${course.id}`} onClick={() => bulkGrade(editGrade, true)}
                className="px-2.5 py-0.5 rounded-full font-display font-bold border transition-colors bg-[#CCFF00] text-[#000000] border-[#CCFF00] hover:brightness-95">
                Select all {gLabel(editGrade)}
              </button>
              <button data-testid={`quickselect-clear-${course.id}`} onClick={() => bulkGrade(editGrade, false)}
                className="px-2.5 py-0.5 rounded-full font-display font-bold border transition-colors bg-white text-[#000000] border-[#C4C8CE] hover:bg-[#F4F4F5]">
                Clear all {gLabel(editGrade)}
              </button>
              <span className="text-[#3F6212]">or click lessons one by one · <span className="text-[#000000]">lime outline = on, greyed = off</span></span>
            </>
          : <span className="text-[#000000]">Pick a grade, then click lessons (or "Select all") to set them for that grade.</span>}
      </div>

      {showAdd && (
        <div className="p-3 rounded-xl bg-[#DCDEE2] border border-[#C4C8CE]">
          <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-2">Library — click to add to this course</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-52 overflow-y-auto pr-1">
            {available.map(a => (
              <button key={a.id} type="button" data-testid={`add-lesson-${a.id}`} onClick={() => addLesson(a.id)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#FFFFFF] border border-[#C4C8CE] hover:border-[#CCFF00] text-left transition-all cursor-pointer">
                <Plus size={14} weight="bold" color="#CCFF00" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">{a.title}</div>
                  <div className="text-[10px] text-[#000000]">{a.pillar ? labelOf(a.pillar) : introLabel}</div>
                </div>
              </button>
            ))}
            {available.length === 0 && <div className="text-xs italic text-[#000000]">All library lessons are already in this course.</div>}
          </div>
        </div>
      )}

      {loading && <div className="text-[#000000] text-sm">Loading matrix…</div>}

      {/* Matrix table */}
      {!loading && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse table-fixed" style={{ minWidth: 36 + columns.length * 176 }} data-testid={`matrix-table-${course.id}`}>
            <thead>
              <tr>
                <th className="p-2 text-[10px] uppercase tracking-[0.12em] text-[#000000]" style={{ width: 36 }}>#</th>
                {columns.map(col => (
                  <th key={col.key} className="text-left p-2" style={{ width: `${(100 / columns.length).toFixed(3)}%` }}>
                    <span className="chip text-[10px] font-bold" style={pillarChipStyle(col.color)}>{col.name}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxRows }).map((_, r) => (
                <tr key={r} className="border-t border-[#C4C8CE]" data-testid={`matrix-row-${r + 1}`}>
                  <td className="p-2 align-middle text-center font-display font-bold text-[#000000]">{r + 1}</td>
                  {columns.map(col => {
                    const ls = (columnGroups[col.key] || [])[r];
                    const cellKey = `${col.key}:${r}`;
                    const isOver = dragOverCell === cellKey;
                    const a = ls ? assetMap[ls.lesson_asset_id] : null;
                    const dim = ls && !visibleForPreview(ls.lesson_asset_id);
                    return (
                      <td
                        key={col.key} className="p-2 align-top"
                        onDragOver={(e) => { if (dragRef.current) { e.preventDefault(); setDragOverCell(cellKey); } }}
                        onDragLeave={() => { if (isOver) setDragOverCell(null); }}
                        onDrop={(e) => { e.preventDefault(); if (dragRef.current) moveCard(dragRef.current, col.key, ls?.lesson_asset_id || null); }}
                      >
                        {ls ? (
                          <div style={{ opacity: dim ? 0.35 : 1, boxShadow: isOver ? "0 0 0 2px #CCFF00" : "none", borderRadius: 12 }}>
                            <LessonCell
                              lesson={ls} asset={a} courseId={course.id}
                              grades={grid[ls.lesson_asset_id] || new Set()}
                              editGrade={editGrade}
                              warnStats={warnStats[ls.lesson_asset_id]}
                              onDragStart={() => { dragRef.current = ls.lesson_asset_id; }}
                              onDragEnd={() => { dragRef.current = null; setDragOverCell(null); }}
                              onToggle={toggleGrade} onOpen={() => nav(`/admin/lesson/${ls.lesson_asset_id}?course=${course.id}`)}
                              onRemove={() => removeLesson(ls.lesson_asset_id)}
                            />
                          </div>
                        ) : (
                          <div
                            className="h-10 rounded-lg border border-dashed transition-all"
                            style={{ borderColor: isOver ? "#CCFF00" : "transparent", background: isOver ? "rgba(204,255,0,0.12)" : "transparent" }}
                            data-testid={`matrix-empty-${col.key}-${r}`}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {maxRows === 0 && (
                <tr><td colSpan={columns.length + 1} className="p-4 text-center text-sm text-[#000000] italic">No lessons yet. Click "Create lesson" or "Add from library".</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LessonCell({ lesson, asset, grades, onToggle, onOpen, onRemove, onDragStart, onDragEnd, editGrade, warnStats }) {
  const title = asset?.title || "(missing asset)";
  const hasQuiz = asset?.quiz && (asset.quiz.questions || []).length > 0;
  const vidCount = (asset?.videos || []).length || (asset?.video_url ? 1 : 0);
  const editActive = !!editGrade;
  const editOn = editActive && grades.has(editGrade);
  const cardStyle = editActive
    ? (editOn
        ? { cursor: "pointer", border: "2px solid #CCFF00", background: "#FFFFFF", boxShadow: "0 0 0 1px #CCFF00" }
        : { cursor: "pointer", border: "2px solid #C4C8CE", background: "#ECEDEF", opacity: 0.45 })
    : { cursor: "grab", border: "1px solid #C4C8CE", background: "#FFFFFF" };
  const stop = (fn) => (e) => { e.stopPropagation(); fn && fn(); };
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={editActive ? () => onToggle(lesson.lesson_asset_id, editGrade) : undefined}
      className="rounded-xl p-2.5 flex flex-col gap-2 transition-all w-full"
      style={cardStyle}
      data-testid={`lesson-cell-${lesson.lesson_asset_id}`}
      data-edit-state={editActive ? (editOn ? "on" : "off") : "none"}
      title={editActive ? `Click to turn ${editGrade} ${editOn ? "OFF" : "ON"} for this lesson` : undefined}
    >
      <div className="flex items-start justify-between gap-1">
        <button type="button" onClick={editActive ? undefined : stop(onOpen)} data-testid={`open-lesson-${lesson.lesson_asset_id}`} className={`min-w-0 text-left text-sm font-semibold text-[#000000] flex items-start gap-1 bg-transparent border-0 p-0 ${editActive ? "cursor-pointer" : "cursor-pointer hover:text-[#00B8D4]"}`}>
          <DotsSixVertical size={13} color="#A1A1AA" className="shrink-0 mt-0.5" data-testid={`drag-handle-${lesson.lesson_asset_id}`} />
          {!editActive && <ArrowSquareOut size={13} className="shrink-0 mt-0.5" />} <span className="break-words leading-snug line-clamp-2 min-h-[2.75em]" title={title}>{title}</span>
        </button>
        <button className="btn-ghost p-0.5" onClick={stop(onRemove)} data-testid={`remove-lesson-${lesson.lesson_asset_id}`} title="Remove from course"><Trash size={12} color="#FF5100" /></button>
      </div>
      <div className="flex items-center gap-1.5 text-[10px] text-[#000000] h-5">
        {asset?.thumbnail_url && (
          <img src={asset.thumbnail_url} alt={title} className="h-5 aspect-video object-cover rounded border border-[#C4C8CE] shrink-0" data-testid={`lesson-cell-thumb-${lesson.lesson_asset_id}`} />
        )}
        <span className="flex items-center gap-0.5"><FilmSlate size={11} /> {vidCount}</span>
        {(asset?.swap_rules || []).some(r => r.scope === "language" && r.scope_value) && (
          <span className="flex items-center gap-0.5 font-bold" style={{ color: "#007A8F" }}
            data-testid={`lesson-lang-${lesson.lesson_asset_id}`}
            title={`This lesson has a translated version: ${[...new Set(asset.swap_rules.filter(r => r.scope === "language" && r.scope_value).map(r => r.scope_value.toUpperCase()))].join(", ")}`}>
            <Translate size={11} weight="bold" /> {[...new Set(asset.swap_rules.filter(r => r.scope === "language" && r.scope_value).map(r => r.scope_value.toUpperCase()))].join(" ")}
          </span>
        )}
        {hasQuiz && <span className="flex items-center gap-0.5 text-[#7a8a00]"><Question size={11} /> quiz</span>}
        {asset?.heavy_content && <span className="flex items-center gap-0.5 text-[#FF5100]"><Warning size={11} /> heavy</span>}
        {asset?.heavy_content && (warnStats?.back || 0) > 0 && (
          <span className="flex items-center gap-0.5 font-bold text-[#B91C1C]"
            data-testid={`heavy-backouts-${lesson.lesson_asset_id}`}
            title={`Warning choices: ${warnStats.back} stepped back · ${warnStats.ready || 0} went ahead. High step-back counts may mean this lesson needs gentler framing.`}>
            <ArrowUUpLeft size={11} weight="bold" /> {warnStats.back}
          </span>
        )}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {GRADES.map(g => {
          const on = grades.has(g);
          const isEditG = editActive && g === editGrade;
          return (
            <button key={g} type="button" data-testid={`grade-chip-${lesson.lesson_asset_id}-${g}`} data-state={on ? "on" : "off"} aria-pressed={on} onClick={stop(() => onToggle(lesson.lesson_asset_id, g))}
              className="py-0.5 rounded text-[11px] font-display font-bold tracking-tighter text-center leading-none transition-colors"
              style={{ background: on ? "#CCFF00" : "#ECEDEF", color: on ? "#1F2937" : "#A1A1AA", border: (isEditG ? "2px solid #00B8D4" : "1px solid " + (on ? "#CCFF00" : "#C4C8CE")), cursor: "pointer", fontSize: g === "DEMO" ? 9 : undefined }}>
              {gLabel(g)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
